import { TimelineCustomer, TimelineSite, TimelineOrder } from '../types/timeline';
import { customerService } from '../feature/customer/customerService';
import { siteService } from '../feature/site/siteService';
import { orderService } from '../feature/orders/orderService';

class CustomerTimelineService {
  /**
   * Fetch customer with sites and orders data for timeline view
   */
  async getCustomerWithTimelineData(customerId: string): Promise<TimelineCustomer> {
    try {
      // Fetch customer data
      console.log('Fetching customer data for ID:', customerId);
      const customerResponse = await customerService.getCustomer(customerId);
      console.log('Customer response:', customerResponse);
      
      if (!customerResponse.success) {
        throw new Error(`Failed to fetch customer data: ${customerResponse.error || 'Unknown error'}`);
      }
      const customer = customerResponse.data.customer;
      console.log('Customer data:', customer);

      // Fetch sites for this customer
      console.log('Fetching sites for customer:', customerId);
      const sitesResponse = await siteService.getSites({ customerId });
      console.log('Sites response:', sitesResponse);
      
      if (!sitesResponse.success) {
        throw new Error(`Failed to fetch sites data: ${sitesResponse.error || 'Unknown error'}`);
      }
      
      // Fix: Access sites from the correct data structure with fallback
      const sites = sitesResponse.data || sitesResponse.data || [];
      console.log('Sites found for customer:', sites.length, sites);

      // Fetch orders for each site
      const sitesWithOrders: TimelineSite[] = await Promise.all(
        sites.map(async (site: any) => {
          try {
            console.log(`Processing site ${site.id} (${site.name})`);
            
            // Use existing getOrdersByCustomer API and filter by site
            const ordersResponse = await orderService.getOrdersByCustomer(customerId);
            console.log(`Orders response for customer ${customerId}:`, ordersResponse);
            
            const allOrders = ordersResponse.success ? ordersResponse.data || [] : [];
            console.log('All orders for customer:', allOrders.length, allOrders);
            
            // Filter orders by site_id (handle different possible field names)
            const orders = allOrders.filter((order: any) => 
              order.site_id === site.id || 
              order.siteId === site.id || 
              order.site?.id === site.id
            );
            console.log(`Filtered orders for site ${site.id}:`, orders.length, orders);
            
            return {
              id: site.id,
              name: site.name,
              address: site.address,
              city: site.city,
              state: site.state,
              contactPerson: site.contactPerson,
              contactPhone: site.contactPhone,
              status: site.status ? 'active' : 'inactive',
              customer: {
                id: customer.id,
                name: customer.name,
                phone: customer.phone,
                email: customer.email,
              },
              orders: orders.map((order: any) => this.transformOrderToTimelineOrder(order)),
            };
          } catch (error) {
            console.error(`Failed to fetch orders for site ${site.id}:`, error);
            return {
              id: site.id,
              name: site.name,
              address: site.address,
              city: site.city,
              state: site.state,
              contactPerson: site.contactPerson,
              contactPhone: site.contactPhone,
              status: site.status ? 'active' : 'inactive',
              customer: {
                id: customer.id,
                name: customer.name,
                phone: customer.phone,
                email: customer.email,
              },
              orders: [],
            };
          }
        })
      );

      const result = {
        id: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zipCode: customer.zipCode,
        contactPerson: customer.contactPerson,
        contactPhone: customer.contactPhone,
        contactEmail: customer.contactEmail,
        status: customer.status ? 'active' : 'inactive',
        type: customer.type,
        sites: sitesWithOrders,
      };
      
      console.log('Customer timeline data result:', result);
      console.log('Total sites with orders:', sitesWithOrders.length);
      console.log('Total orders across all sites:', sitesWithOrders.reduce((total, site) => total + (site.orders?.length || 0), 0));
      
      return result;
      // return customerResponse;
    } catch (error) {
      console.error('Error fetching customer timeline data:', error);
      throw error;
    }
  }

  /**
   * Fetch site with orders data for timeline view
   */
  async getSiteWithTimelineData(siteId: string): Promise<TimelineSite> {
    try {
      // Fetch site data using getSiteById for single site lookup
      const siteResponse = await siteService.getSiteById(siteId);
      if (!siteResponse.success || !siteResponse.data) {
        throw new Error('Failed to fetch site data');
      }
      const site = siteResponse.data;
      // console.log('Site data fetched id name:', site.id, site.name);
      console.log('Site data fetched:', site);
      console.log('Site data fetched site. :', site?.site);
      // Fetch customer data for this site
      let customer = null;
      if (site.customerId) {
        try {
          const customerResponse = await customerService.getCustomer(site.customerId);
          if (customerResponse.success) {
            customer = customerResponse.data;
          } else {
            console.warn('Failed to fetch customer data for site:', customerResponse.error);
          }
        } catch (error) {
          console.error('Failed to fetch customer data for site:', error);
        }
      }

      // Fetch orders for this site
      let orders: any[] = [];
      if (site.customerId) {
        try {
          console.log('Fetching orders for customer:', site.customerId, 'site:', siteId);
          const ordersResponse = await orderService.getOrdersByCustomer(site.customerId);
          console.log('Orders response:', ordersResponse);
          if (ordersResponse.success) {
            const allOrders = ordersResponse.data || [];
            console.log('All orders for customer:', allOrders.length, allOrders);
            orders = allOrders.filter((order: any) => order.site_id === siteId);
            console.log('Filtered orders for site:', orders.length, orders);
          } else {
            console.warn('Failed to fetch orders for site:', ordersResponse.error);
          }
        } catch (error) {
          console.error('Failed to fetch orders for site:', error);
        }
      }

      const result = {
        id: site?.site?.id,
        name: site?.site?.name,
        address: site?.site?.address,
        city: site?.site?.city,
        state: site?.site?.state,
        contactPerson: site?.site?.contactPerson,
        contactPhone: site?.site?.contactPhone,
        status: site?.site?.status ? 'active' : 'inactive',
        customer: site?.site?.customer ? {
          id: site?.site?.customer.id,
          name: site?.site?.customer?.name,
          phone: site?.site?.customer?.phone,
          email: site?.site?.customer?.email,
        } : undefined,
        orders: orders.map((order: any) => this.transformOrderToTimelineOrder(order)),
      };
      console.log('Site timeline data result:', result);
      return result;
    } catch (error) {
      console.error('Error fetching site timeline data:', error);
      // Provide more specific error message
      if (error instanceof Error) {
        throw new Error(`Failed to fetch site timeline data: ${error.message}`);
      } else {
        throw new Error('Failed to fetch site timeline data: Unknown error occurred');
      }
    }
  }

  /**
   * Fetch order with products and items data for timeline view
   */
  async getOrderWithTimelineData(orderId: string): Promise<TimelineOrder> {
    try {
      const response = await orderService.getOrder(orderId);
      if (!response.success) {
        throw new Error('Failed to fetch order data');
      }
      return this.transformOrderToTimelineOrder(response.data);
    } catch (error) {
      console.error('Error fetching order timeline data:', error);
      throw error;
    }
  }

  /**
   * Transform order data to timeline order format
   */
  private transformOrderToTimelineOrder(order: any): TimelineOrder {
    return {
      id: order.id,
      orderNumber: order.orderNumber || order.orderNumberNew,
      orderNumberNew: order.orderNumberNew,
      customer: order.customer ? {
        id: order.customer.id,
        name: order.customer.name,
        phone: order.customer.phone,
        email: order.customer.email,
        address: order.customer.address,
      } : undefined,
      site: order.site ? {
        id: order.site.id,
        name: order.site.name,
        address: order.site.address,
        city: order.site.city,
        state: order.site.state,
        contactPerson: order.site.contactPerson,
        contactPhone: order.site.contactPhone,
      } : undefined,
      status: order.status || 'pending',
      priority: order.priority || 'normal',
      totalAmount: order.totalAmount || 0,
      paymentStatus: order.paymentStatus || 'unpaid',
      createdAt: order.createdAt || new Date().toISOString(),
      products: (order.products || []).map((product: any) => this.transformProductToTimelineProduct(product)),
    };
  }

  /**
   * Transform product data to timeline product format
   */
  private transformProductToTimelineProduct(product: any): any {
    return {
      id: product.id,
      name: product.name || `Product ${product.id.slice(-4)}`,
      type: product.type || 'Unknown',
      description: product.description || '',
      status: product.status || 'pending',
      total_items: product.total_items || 0,
      completed_items: product.completed_items || 0,
      unit_price: product.unit_price || null,
      quantity: product.quantity || 0,
      label: product.label,
      location: product.location,
      evaOrder: product.evaOrder,
      frame: (product.frame_items || []).map((item: any) => this.transformItemToTimelineItem(item)),
      panel: (product.panel_items || []).map((item: any) => this.transformItemToTimelineItem(item)),
      panelWindow: (product.panel_window_items || []).map((item: any) => this.transformItemToTimelineItem(item)),
      net: (product.net_items || []).map((item: any) => this.transformItemToTimelineItem(item)),
      openableType: (product.openable_type_items || []).map((item: any) => this.transformItemToTimelineItem(item)),
      items: (product.items || []).map((item: any) => this.transformItemToTimelineItem(item)),
    };
  }

  /**
   * Transform item data to timeline item format
   */
  private transformItemToTimelineItem(item: any): any {
    return {
      id: item.id,
      itemNumber: item.itemNumber || item.item_number,
      type: item.type || 'Unknown',
      status: item.status || 'pending',
      currentStage: item.current_stage || item.currentStage,
      qrCode: item.qr_code || item.qrCode,
      assignedTo: item.assigned_to || item.assignedTo,
      width: item.width || 0,
      height: item.height || 0,
      material: item.material || '',
      color: item.color || '',
      finish: item.finish || '',
      logs: (item.logs || []).map((log: any) => this.transformLogToTimelineEvent(log)),
      createdAt: item.created_at || item.createdAt,
      completedAt: item.completed_at || item.completedAt,
      estimatedHours: item.estimated_hours || item.estimatedHours,
      actualHours: item.actual_hours || item.actualHours,
      quantity: item.quantity || 1,
    };
  }

  /**
   * Transform log data to timeline event format
   */
  private transformLogToTimelineEvent(log: any): any {
    return {
      id: log.id,
      stage: log.stage,
      status: log.status || 'pending',
      user_id: log.user_id || log.userId,
      user_name: log.user_name || log.userName,
      timestamp: log.timestamp || new Date().toISOString(),
      notes: log.notes || '',
      duration_hours: log.duration_hours || log.durationHours,
      qr_code: log.qr_code || log.qrCode,
      stage_order: log.stage_order || log.stageOrder || 0,
      previous_stage: log.previous_stage || log.previousStage,
      next_stage: log.next_stage || log.nextStage,
    };
  }

}

export default new CustomerTimelineService();
